import {action, computed, observable, runInAction} from 'mobx';
import isEmpty from 'lodash/isEmpty';
import Papa from 'papaparse';
import {DialogTypes, SchoolUserMode} from '../../../constants/enums';
import GenericFormStore from '../../../stores/FormStore';
import {uploadToS3} from '../../../utils/awsUtility';
import {go} from '../../../utils/navigationUtility';
import {getId, getUser} from '../../../utils/userUtility';
import dialogStore from '../../dialog/store';
import manageStore from '../../../stores/ManageStore';
import mainStore from '../store';
import appStore from '../../app/store';
import request from '../../../utils/request';
import {SCHOOL} from '../../../constants/urlConstants';
import attachStudentStore from "./teachers/AttachStudentStore";

class SchoolStore extends GenericFormStore {
    @observable schoolList = [];
    @observable activeSchool = {};
    @observable isFetching = false;
    @observable isProcessing = false;
    @observable isAvatarSaving = false;
    @observable searchStr = '';
    @observable usersSearchStr = '';
    @observable form = this.initForm();
    @observable csvErrors = [];
    @observable userMode = SchoolUserMode.Student;
    @observable isUserEditable = false;
    @observable csvErrorMessage = '';
    @observable csvImportResults = [];

    initForm = () => ({
        fields: {
            name: {
                value: '',
                rule: 'required'
            },
            avatar: {
                value: '/avatar/default/15.jpg',
                rule: 'string'
            },
            sendChatToAdmin: {
                value: false,
                rule: 'boolean'
            },
            useDefaultPassword: {
                value: false,
                rule: 'boolean'
            },
            isolateTeachers: {
                value: false,
                rule: 'boolean'
            },
            hideDiscover: {
                value: false,
                rule: 'boolean'
            },
            isSilo: {
                value: false,
                rule: 'boolean'
            },
            sharingEnabled: {
                value: true,
                rule: 'boolean'
            },
            enableSocialShare: {
                value: false,
                rule: 'boolean'
            }
        },
        meta: {
            isValid: false,
            isEditableId: null
        }
    });

    @computed
    get schools() {
        return this.schoolList.filter(school => (
            school.name.toLowerCase().includes(this.searchStr)
        ));
    }

    @computed
    get teachers() {
        return this.activeSchool ? this.filterUsersBySearchStr(this.activeSchool.teachers) : [];
    }

    @computed
    get students() {
        return this.activeSchool ? this.filterUsersBySearchStr(this.activeSchool.students) : [];
    }

    @computed
    get admins() {
        return this.activeSchool ? this.filterUsersBySearchStr(this.activeSchool.admins) : [];
    }

    @computed
    get allUsers() {
        return this.activeSchool ? [...this.activeSchool.teachers, ...this.activeSchool.students] : [];
    }

    @computed
    get filteredUsers() {
        return [...this.teachers, ...this.students];
    }

    @computed
    get hideDiscover() {
        return this.activeSchool ? this.activeSchool.hideDiscover : false;
    }

    filterUsersBySearchStr = (users = []) => {
        return users.filter(user => (
            `${user.name} ${user.email}`.toLowerCase().includes(this.usersSearchStr)
        ));
    };

    @action setUserMode = (mode) => {
        this.userMode = mode;
    };

    @action setUserEditable = (val) => {
        this.isUserEditable = val;
    };

    @action setForm = (school) => {
        if (school) {
            this.form.fields.name.value = school.name;
            this.form.fields.avatar.value = school.avatar;
            this.form.fields.sendChatToAdmin.value = school.sendChatToAdmin;
            this.form.fields.useDefaultPassword.value = school.useDefaultPassword;
            this.form.fields.isolateTeachers.value = school.isolateTeachers;
            this.form.fields.hideDiscover.value = school.hideDiscover;
            this.form.fields.isSilo.value = school.isSilo;
            this.form.fields.sharingEnabled.value = school.sharingEnabled;
            this.form.fields.enableSocialShare.value = school.enableSocialShare;
            this.form.meta.isEditableId = school.id;
        }
    };

    @action resetForm = () => {
        this.form = this.initForm();
    };

    @action setUserInstrument = (i) => {
        this.userForm.fields.instrument.value = i;
    };

    @action searchSchools = (val) => {
        this.searchStr = val;
    };

    @action searchUsers = (val) => {
        this.usersSearchStr = val;
    };

    @action setActiveSchool = (school) => {
        this.activeSchool = school;
    };

    @action fetchSchoolList = async (force = false) => {
        if ((this.isFetching || !isEmpty(this.schoolList)) && !force) {
            return;
        }
        this.isFetching = true;
        try {
            const list = await request(SCHOOL);
            runInAction(() => {
                this.schoolList = list.sort((a, b) => Date.parse(b.created) - Date.parse(a.created));
            });
        } catch (err) {
            console.warn(err.message);
        } finally {
            runInAction(() => {
                this.isFetching = false;
            });
        }
    };

    @action fetchSchool = async (id, redirect = true) => {
        this.isFetching = true;
        try {
            //TODO update teacher map to return teachers from same school
            const school = await request(`${SCHOOL}/${id || this.activeSchool.id}`);
            school.students = school.students ? school.students.sort((a, b) => {
                return (a.email || '').toLowerCase() > (b.email || '').toLowerCase() ? 1 : -1;
            }) : [];
            let studentTeacherMap = (school.relations || []).reduce((res, studentTeacher) => {
                if (res.byTeacher[studentTeacher.teacherId]) {
                    res.byTeacher[studentTeacher.teacherId].push(studentTeacher.studentId);
                } else {
                    res.byTeacher[studentTeacher.teacherId] = [studentTeacher.studentId];
                }
                if (res.byStudent[studentTeacher.studentId]) {
                    res.byStudent[studentTeacher.studentId].push(studentTeacher.teacherId);
                } else {
                    res.byStudent[studentTeacher.studentId] = [studentTeacher.teacherId];
                }

                return res;
            }, {byTeacher: {}, byStudent: {}})
            school.teachers = school.teachers ? school.teachers.map(teacher => {
                teacher.students = studentTeacherMap.byTeacher[teacher.id] || [];
                return teacher;
            }) : [];
            school.students = school.students ? school.students.map(student => {
                student.teachers = studentTeacherMap.byStudent[student.id] || [];
                return student;
            }) : [];

            this.setActiveSchool(school);
            return school;
        } catch (err) {
            console.warn(err.message);
            if (redirect) {
                go('/home/schools');
            }
        } finally {
            runInAction(() => {
                this.isFetching = false;
            });
        }
    };

    @action addSchool = async () => {
        this.isProcessing = true;
        mainStore.toggleProgressLoader(true);
        try {
            const {name, avatar} = this.form.fields;
            const newSchool = await request(
                SCHOOL,
                {
                    method: 'POST',
                    body: {
                        name: name.value,
                        avatar: avatar.value,
                        sendChatToAdmin: this.form.fields.sendChatToAdmin.value,
                        useDefaultPassword: this.form.fields.useDefaultPassword.value,
                        sharingEnabled: this.form.fields.sharingEnabled.value,
                        enableSocialShare: this.form.fields.enableSocialShare.value,
                    }
                }
            );
            runInAction(() => {
                this.schoolList.unshift(newSchool);
            });
            appStore.createAlertFromMessageId('success', 'schools.form.wasAdded', 2000);
        } catch (err) {
            console.warn(err.message);
        } finally {
            runInAction(() => {
                this.isProcessing = false;
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action editSchool = async () => {
        this.isProcessing = true;
        mainStore.toggleProgressLoader(true);
        try {
            const {fields, meta} = this.form;
            const {name, avatar} = await request(
                `${SCHOOL}/${meta.isEditableId}`,
                {
                    method: 'POST',
                    body: {
                        name: fields.name.value,
                        avatar: fields.avatar.value,
                        sendChatToAdmin: fields.sendChatToAdmin.value,
                        useDefaultPassword: fields.useDefaultPassword.value,
                        isolateTeachers: fields.isolateTeachers.value,
                        hideDiscover: fields.hideDiscover.value,
                        isSilo: fields.isSilo.value,
                        sharingEnabled: fields.sharingEnabled.value,
                        enableSocialShare: fields.enableSocialShare.value,
                    }
                }
            );
            this.setActiveSchool({...this.activeSchool, name, avatar});
        } catch (err) {
            console.warn(err.message);
        } finally {
            runInAction(() => {
                this.isProcessing = false;
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action migrateTeachers = async (teacherEmails) => {
        this.isProcessing = true;
        mainStore.toggleProgressLoader(true);
        try {
            const res = await request(
                `${SCHOOL}/${this.activeSchool.id}/move`,
                {
                    method: 'POST',
                    body: {
                        emails: teacherEmails,
                    }
                },
                true
            );
            if (res.err) {
                return {err: res.err};
            }
            return {success: true};
        } catch (err) {
            console.warn(err.message);
            return {err: err};
        } finally {
            runInAction(() => {
                this.isProcessing = false;
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action removeSchool = async (id) => {
        this.isProcessing = true;
        mainStore.toggleProgressLoader(true);
        try {
            await request(`${SCHOOL}/${id}`, {method: 'DELETE'});
            go('/home/schools');
        } catch (err) {
            console.warn(err.message);
        } finally {
            runInAction(() => {
                this.isProcessing = false;
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action updateSchoolAvatar = async (file, isEdit) => {
        if (!isEdit) return;
        const fileId = `${getId()}_${Date.now()}`;
        this.isAvatarSaving = true;
        try {
            await uploadToS3(
                file,
                fileId,
                file.type,
                null,
                'musico-public-bucket/avatar',
                true
            );
            this.onChange('avatar', `/avatar/${fileId}`);
            this.editSchool();
        } catch (err) {
            console.warn(err.message);
        } finally {
            runInAction(() => {
                this.isAvatarSaving = false;
            });
        }
    };

    @action getDataFromCsv = (file) => {
        return new Promise((resolve, reject) => {
            Papa.parse(file, {
                skipEmptyLines: true,
                header: true,
                complete: async (results) => {
                    if (!isEmpty(results.errors)) {
                        this.setCsvErrors(results.errors);
                        reject();
                    } else {
                        //await this.addUsersToSchool(results.data, type);
                        console.log(results.data);
                        this.setCsvErrors([]);
                        resolve(results.data);
                    }
                }
            });
        });
    };

    getAllDataFromCsv = (file) => {
        return new Promise((resolve) => {
            Papa.parse(file, {
                skipEmptyLines: true,
                header: true,
                complete: (results) => {
                    resolve(results.data);
                }
            });
        });
    };

    addUsersByCsv = async (csvFile) => {
        const data = await this.getAllDataFromCsv(csvFile) || [];
        if (isEmpty(data)) {
            runInAction(() => {
                this.csvErrorMessage = 'CSV file is empty';
            });
            setTimeout(() => {
                runInAction(() => {
                    this.csvErrorMessage = '';
                });
            }, 3000);
            return;
        }
        const userList = data.map((row = {}) => {
            let user = {};
            if (row['Email']) {
                user.email = row['Email'];
            } else if (Object.keys(row)[0]) {
                user.email = row[Object.keys(row)[0]];
            }
            if (row['Full Name']) {
                user.name = row['Full Name'];
            } else if (Object.keys(row)[1]) {
                user.name = row[Object.keys(row)[1]];
            }
            if (row['Main Instrument (optional)']) {
                user.instrument = row['Main Instrument (optional)'];
            } else if (Object.keys(row)[2]) {
                user.instrument = row[Object.keys(row)[2]];
            }
            return user;
        });
        await this.addUsersBySchoolAdmin(userList, true);
    };

    addSingleUser = async (userFields) => {
        let user = {};
        if (userFields.name.value) {
            user.name = userFields.name.value;
        }
        if (userFields.familyName.value) {
            user.name = user.name ? `${user.name} ${userFields.familyName.value}` : userFields.familyName.value;
        }
        if (userFields.email.value) {
            user.email = userFields.email.value;
        }
        if (userFields.instrument.value) {
            user.instrument = userFields.instrument.value;
        }
        let res = await this.addUsersBySchoolAdmin([user], false);
        if (!this.isUserEditable && this.userMode === SchoolUserMode.Teacher && this.activeSchool?.isSilo) {
            if (res && res.length > 0) {
                const teacher = {...res[0].user, students: []};
                attachStudentStore.onToggleAssignDialog(teacher);
            }
        }
    };

    @action addUsersBySchoolAdmin = async (data, isBulk) => {
        const schoolId = (getUser() || {}).schoolId || (this.activeSchool && this.activeSchool.id);
        this.isProcessing = true;
        mainStore.toggleProgressLoader(true);
        try {
            let res = await request(
                `${SCHOOL}/${schoolId}/${this.userMode}`,
                {method: 'POST', body: {data}}
            );
            if (res && res.length > 0) {
                res.map(item => {
                    if (item.isExisting || item.newUser) {
                        manageStore.updateList(item.user);
                        appStore.createAlertFromMessageId('success', 'schools.import.toast.success', {email: item.user.email}, 3000);
                    } else {
                        appStore.createAlertFromMessageId('warning', 'schools.import.toast.error', {email: item.user.email, error: item.details}, 3000);
                    }
                });
            }

            runInAction(() => {
                if (isBulk) {
                    this.setCsvImportResults(res);
                    dialogStore.openDialog(DialogTypes.ImportUsersResult);
                }
            });
            return res;
        } catch (err) {
            console.warn(err.message);
            appStore.createAlertFromMessageId('error', 'schools.import.toast.errors', 3000);
        } finally {
            runInAction(() => {
                this.isProcessing = false;
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action addUsersToSchool = async (csvFile, path) => {
        this.isProcessing = true;
        mainStore.toggleProgressLoader(true);
        try {
            const data = await this.getDataFromCsv(csvFile);
            await request(
                `${SCHOOL}/${this.form.meta.isEditableId}/${path}`,
                {method: 'POST', body: {data}}
            );
            this.fetchSchool();
        } catch (err) {
            console.warn(err.message);
        } finally {
            runInAction(() => {
                this.isProcessing = false;
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action addAllUsersAndGroupsToSchool = async (csvFile) => {
        this.isProcessing = true;
        mainStore.toggleProgressLoader(true);
        try {
            const data = this.getDataFromCsv(csvFile);
            await request(
                `${SCHOOL}/${this.form.meta.isEditableId}/all`,
                {method: 'POST', body: {data}}
            );
            this.fetchSchool();
        } catch (err) {
            console.warn(err.message);
        } finally {
            runInAction(() => {
                this.isProcessing = false;
                mainStore.toggleProgressLoader(false);
            });
        }
    };

    @action setCsvErrors = (val) => {
        this.csvErrors = val;
    };

    @action setCsvImportResults = (val) => {
        this.csvImportResults = val;
    }
}

const schoolStore = new SchoolStore();
export default schoolStore;
